import type { Topology, GeometryCollection } from "topojson-specification";

// Cache for different resolutions (memory cache)
let cachedWorldData10m: Topology | null = null;
let cachedWorldData50m: Topology | null = null;
let loadPromise10m: Promise<Topology> | null = null;
let loadPromise50m: Promise<Topology> | null = null;

export type MapResolution = '10m' | '50m';

// Cache API name for persistent cross-session caching
const CACHE_NAME = 'witw-map-cache-v1';

// Map resolution to CDN URLs
const MAP_URLS: Record<MapResolution, string> = {
  '10m': 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-10m.json',
  '50m': 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-50m.json',
};

/**
 * Try to get data from Cache API (persistent storage).
 * Returns null if not cached or Cache API unavailable.
 */
async function getFromCacheAPI(url: string): Promise<Topology | null> {
  if (typeof caches === 'undefined') return null;

  try {
    const cache = await caches.open(CACHE_NAME);
    const cachedResponse = await cache.match(url);

    if (cachedResponse) {
      const data = await cachedResponse.json();
      return data as Topology;
    }
  } catch (err) {
    console.warn('Cache API read failed:', err);
  }

  return null;
}

/**
 * Store data in Cache API for persistent caching.
 */
async function storeInCacheAPI(url: string, data: Topology): Promise<void> {
  if (typeof caches === 'undefined') return;

  try {
    const cache = await caches.open(CACHE_NAME);
    const response = new Response(JSON.stringify(data), {
      headers: {
        'Content-Type': 'application/json',
        'X-Cached-At': new Date().toISOString(),
      },
    });
    await cache.put(url, response);
  } catch (err) {
    console.warn('Cache API write failed:', err);
  }
}

/**
 * Validate that topology data is well-formed.
 * Checks for:
 * - Required structure (objects.countries with geometries)
 * - Non-empty geometry arrays
 * - Valid arcs array
 */
function isValidTopology(data: unknown): data is Topology {
  if (!data || typeof data !== 'object') return false;

  const topology = data as Partial<Topology>;

  // Check required top-level properties
  if (topology.type !== 'Topology') return false;
  if (!Array.isArray(topology.arcs)) return false;
  if (!topology.objects || typeof topology.objects !== 'object') return false;

  // Check countries object exists
  const countries = topology.objects.countries as GeometryCollection | undefined;
  if (!countries || !Array.isArray(countries.geometries)) return false;

  // Check we have a reasonable number of countries (should be ~200+)
  if (countries.geometries.length < 150) {
    console.warn(`Topology has only ${countries.geometries.length} countries, expected ~200+`);
    return false;
  }

  // Check arcs are valid arrays of coordinate pairs
  if (topology.arcs.length === 0) return false;

  // Spot check a few arcs for valid structure
  const sampleArcs = topology.arcs.slice(0, 5);
  for (const arc of sampleArcs) {
    if (!Array.isArray(arc) || arc.length === 0) return false;
    // Each arc should contain coordinate pairs
    const firstPoint = arc[0];
    if (!Array.isArray(firstPoint) || firstPoint.length < 2) return false;
  }

  return true;
}

/**
 * Set memory cache for a resolution.
 */
function setMemoryCache(resolution: MapResolution, data: Topology): void {
  if (resolution === '10m') {
    cachedWorldData10m = data;
  } else {
    cachedWorldData50m = data;
  }
}

/**
 * Clear memory cache and promise for a resolution.
 */
function clearCacheForResolution(resolution: MapResolution): void {
  if (resolution === '10m') {
    cachedWorldData10m = null;
    loadPromise10m = null;
  } else {
    cachedWorldData50m = null;
    loadPromise50m = null;
  }
}

/**
 * Load world data at specified resolution.
 * Uses a 3-tier caching strategy:
 * 1. Memory cache (fastest, lost on page reload)
 * 2. Cache API (persistent, survives page reloads)
 * 3. Network fetch (slowest, used only when cache misses)
 *
 * - 10m: Full resolution (~600KB), includes disputed territories
 * - 50m: Lower resolution (~200KB), faster loading
 */
export const loadWorldData = (resolution: MapResolution = '10m'): Promise<Topology> => {
  const cachedData = resolution === '10m' ? cachedWorldData10m : cachedWorldData50m;
  const currentPromise = resolution === '10m' ? loadPromise10m : loadPromise50m;

  // Tier 1: Check memory cache
  if (cachedData) {
    if (isValidTopology(cachedData)) {
      return Promise.resolve(cachedData);
    }
    // Cached data is invalid, clear it
    console.warn(`Memory cached topology (${resolution}) is invalid, reloading`);
    clearCacheForResolution(resolution);
  }

  // Return existing promise if already loading
  if (currentPromise) {
    return currentPromise;
  }

  const url = MAP_URLS[resolution];

  const promise = (async (): Promise<Topology> => {
    // Tier 2: Try Cache API (persistent storage)
    const cachedFromAPI = await getFromCacheAPI(url);
    if (cachedFromAPI && isValidTopology(cachedFromAPI)) {
      setMemoryCache(resolution, cachedFromAPI);
      return cachedFromAPI;
    }

    // Tier 3: Fetch from network
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch map data: ${response.status}`);
    }

    const data = await response.json() as Topology;

    if (!isValidTopology(data)) {
      throw new Error('Received invalid topology data from server');
    }

    // Store in both caches
    setMemoryCache(resolution, data);
    // Don't await - cache in background
    storeInCacheAPI(url, data);

    return data;
  })();

  // Track the loading promise
  if (resolution === '10m') {
    loadPromise10m = promise;
  } else {
    loadPromise50m = promise;
  }

  // Clear promise on failure to allow retry
  promise.catch((err) => {
    console.error(`Failed to load map data (${resolution})`, err);
    clearCacheForResolution(resolution);
  });

  return promise;
};

/**
 * Get world data synchronously (must call loadWorldData first).
 */
export const getWorldData = (resolution: MapResolution = '10m'): Topology | null => {
  return resolution === '10m' ? cachedWorldData10m : cachedWorldData50m;
};

/**
 * Check if world data has been loaded.
 */
export const isWorldDataLoaded = (resolution: MapResolution = '10m'): boolean => {
  return resolution === '10m' ? cachedWorldData10m !== null : cachedWorldData50m !== null;
};

/**
 * Preload world data - call this on app startup.
 * Preloads both resolutions for optimal performance.
 */
export const preloadWorldData = (): void => {
  loadWorldData('10m').catch(() => {
    // Silently fail - will retry when component needs it
  });
  loadWorldData('50m').catch(() => {
    // Silently fail - will retry when component needs it
  });
};

/**
 * Clear all cached map data (both memory and Cache API).
 * Useful for testing or forcing a fresh load.
 */
export const clearMapCache = async (): Promise<void> => {
  // Clear memory cache
  cachedWorldData10m = null;
  cachedWorldData50m = null;
  loadPromise10m = null;
  loadPromise50m = null;

  // Clear Cache API
  if (typeof caches !== 'undefined') {
    try {
      await caches.delete(CACHE_NAME);
    } catch (err) {
      console.warn('Failed to clear Cache API:', err);
    }
  }
};

/**
 * Get cache statistics for debugging.
 */
export const getMapCacheStats = async (): Promise<{
  memoryCache: { '10m': boolean; '50m': boolean };
  cacheAPI: { '10m': boolean; '50m': boolean };
}> => {
  const stats = {
    memoryCache: {
      '10m': cachedWorldData10m !== null,
      '50m': cachedWorldData50m !== null,
    },
    cacheAPI: {
      '10m': false,
      '50m': false,
    },
  };

  if (typeof caches !== 'undefined') {
    try {
      const cache = await caches.open(CACHE_NAME);
      stats.cacheAPI['10m'] = (await cache.match(MAP_URLS['10m'])) !== undefined;
      stats.cacheAPI['50m'] = (await cache.match(MAP_URLS['50m'])) !== undefined;
    } catch {
      // Cache API not available
    }
  }

  return stats;
};
